import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import {
  CAlert,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
} from "@coreui/react-pro";
import { useCustomerProfile } from "../../api/hooks/useCustomerProfile";
import {
  byStatDescending,
  useTradingStrategies,
} from "../../api/hooks/useTradingStrategies";
import {
  isActiveSubscription,
  useMySubscriptions,
} from "../../api/hooks/useStrategySubscriptions";
import {
  isIncludedInMembership,
  useMyMemberships,
} from "../../api/hooks/useMemberships";
import { useMembershipsEnabled } from "../../api/hooks/usePortalSettings";
import { routePaths } from "../../routes/route-paths";
import {
  countStrategiesByIds,
  groupSides,
  sideGroupKey,
} from "../trading-strategies/strategySides";
import { CrossSellStrip } from "./components/CrossSellStrip";
import { LatestSignals } from "./components/LatestSignals";
import { MembershipPrompt } from "./components/MembershipPrompt";
import { OfferSlot } from "./components/OfferSlot";
import { PartnerOfferSlot } from "./components/PartnerOfferSlot";

export function Dashboard() {
  const { t } = useTranslation("dashboard");
  const { accounts } = useMsal();
  const profile = useCustomerProfile();
  const strategies = useTradingStrategies();
  const subscriptions = useMySubscriptions();
  const subscriptionItems = subscriptions.data ?? [];
  const memberships = useMyMemberships();
  const membershipItems = memberships.data ?? [];
  const membershipsEnabled = useMembershipsEnabled();

  const catalogue = strategies.data ?? [];
  const activeSubs = subscriptionItems.filter((s) => isActiveSubscription(s));
  // Both sides of one strategy are one strategy here, as they are on every list.
  const heldStrategyIds = [
    ...activeSubs.map((s) => s.strategy.id),
    ...membershipItems.flatMap((m) => m.tradingStrategyIds),
  ];
  const heldStrategyCount = countStrategiesByIds(heldStrategyIds, [
    ...catalogue,
    ...activeSubs.map((s) => s.strategy),
  ]);

  // Holding either side of a strategy keeps the whole strategy out of the suggestions.
  const takenStrategies = new Set(
    [
      ...activeSubs.map((s) => s.strategy),
      ...catalogue.filter((tpl) => isIncludedInMembership(tpl.id, membershipItems)),
    ].map(sideGroupKey),
  );
  const suggestions = catalogue.filter((tpl) => !takenStrategies.has(sideGroupKey(tpl)));
  // By the headline figure the nine publish, read from the number rather than parsed back out
  // of the display string. Stands in until strategies carry a rating of their own.
  const byAvgAnnualProfit = byStatDescending("Avg Annual Profit");
  const trending = groupSides(suggestions)
    .sort((a, b) => byAvgAnnualProfit(a[0], b[0]))
    .slice(0, 6);
  const showTrending =
    strategies.isLoading || Boolean(strategies.error) || trending.length > 0;

  const greetingName =
    profile.data?.firstName?.trim() ||
    accounts[0]?.name?.split(" ")[0] ||
    "there";

  return (
    <div>
      <h1 className="mb-1">{t("greeting", { name: greetingName })}</h1>
      <p className="text-body-secondary mb-4">
        You currently have <strong>{heldStrategyCount}</strong>{" "}
        {heldStrategyCount === 1 ? "strategy" : "strategies"}.
      </p>

      {membershipsEnabled && <MembershipPrompt />}

      <LatestSignals />

      {/* Above the strategies, unlike the partner slot below them. A partner offer is an advert for
          someone else; this is money the customer already holds, and it is spent on the very cards
          underneath it — so it belongs where it can be read before they choose. */}
      <OfferSlot />

      {showTrending && (
        <CCard>
          <CCardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{t("crossSell.title")}</strong>
                <div className="text-body-secondary small">
                  {t("crossSell.subtitle")}
                </div>
              </div>
              <Link
                to={routePaths.strategies}
                className="btn btn-outline-primary btn-sm"
              >
                {t("crossSell.viewAll")}
              </Link>
            </div>
          </CCardHeader>
          <CCardBody>
            {strategies.isLoading ? (
              <div className="d-flex justify-content-center py-4">
                <CSpinner color="primary" />
              </div>
            ) : strategies.error ? (
              <CAlert color="warning" className="mb-0">
                Unable to load strategy suggestions.
              </CAlert>
            ) : (
              <CrossSellStrip strategies={trending} />
            )}
          </CCardBody>
        </CCard>
      )}

      {/* Below the strategies, not above. The dashboard's job is selling strategy subscriptions;
          those cards stay the first thing the eye lands on, and the page ends in empty space
          anyway — so this is prominent because nothing else is there, not because it shouted. */}
      <PartnerOfferSlot />
    </div>
  );
}
